import "./OCRMSADecisionModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { formatDate } from "components/Documents/utils/formatters";
import { useEffect, useMemo, useState } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useGetDocumentsQuery } from "services/documents-api";
import {
  useGetMSADecisionsByFileQuery,
  useGetMSADecisionsQuery,
  useSubmitOCRMSADecisionMutation,
} from "services/msa-api";
import type {
  BackgroundJobDataType,
  MSADecisionReviewPayload,
  MSADecisionType,
  PaginatedResponse,
} from "types/DataTypes";

import { KaboodleToaster } from "../common";
import KaboodleModal from "./KaboodleModal";
import OCRMSADecisionReviewForm from "./OCRMSADecisionReviewForm";

const isJobFailed = (job: BackgroundJobDataType) =>
  job.status === "Failed" || job.status === "Error";

type OCRMSADecisionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  msaId: string;
  decisionId: number;
  // Lets a caller that already confirmed this job is finished render the review form immediately instead of
  // waiting on this modal's own separately cached fetch to come back.
  initialReviewPayload?: MSADecisionReviewPayload | null;
};

// Keep in sync with DECISION_TYPE_TO_DOCUMENT_TYPE in
// future_medical/utils/msa_decision_letter_filing.py, since these are the
// only document types the confirm step knows how to file.
const OCR_ELIGIBLE_DOCUMENT_TYPES = ["MSA_APPROVAL", "MSA_COUNTER"];
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  MSA_APPROVAL: "Approval",
  MSA_COUNTER: "Counter",
};

const OCRMSADecisionModal = ({
  isOpen,
  onClose,
  msaId,
  decisionId,
  initialReviewPayload,
}: OCRMSADecisionModalProps) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Lets you retry after a failed job, since otherwise the newest job for
  // this decision would stay failed forever and block the picker.
  const [forcePicker, setForcePicker] = useState(false);

  const [submitOCRMSADecision, { isLoading }] =
    useSubmitOCRMSADecisionMutation();

  // getMSADecisions is typed as MSADecisionType[] but actually returns a
  // paginated envelope at runtime (see the same cast in CMSStatus.tsx).
  const { data: decisionsResponse } = useGetMSADecisionsQuery(
    { msaId },
    { skip: !isOpen },
  );
  const decisions = (
    decisionsResponse as unknown as PaginatedResponse<MSADecisionType>
  )?.results;
  const decision = useMemo(
    () => decisions?.find((d) => d.id === decisionId) || null,
    [decisions, decisionId],
  );

  const fileIdString = decision?.file?.toString() || "";

  // Every decision across every MSA on this file, not just this one, so a
  // scan can be blocked by an unreviewed decision on a different MSA too.
  const {
    data: fileDecisionsResponse,
    isSuccess: isFileDecisionsLoaded,
    isError: isFileDecisionsError,
  } = useGetMSADecisionsByFileQuery(
    { fileId: decision?.file || 0 },
    { skip: !decision?.file || !isOpen },
  );
  const fileDecisions = (
    fileDecisionsResponse as unknown as PaginatedResponse<MSADecisionType>
  )?.results;

  // limit is a ceiling, not pagination: results are filtered client side below.
  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocumentsQuery(
      { fileId: fileIdString, sortBy: "-created", limit: "1000", offset: "0" },
      { skip: !fileIdString || !isOpen },
    );

  // Filtered client-side since the backend's type filter is a single exact match, not an "in" list; the backend
  // re-checks the type server-side too, so this is just for display.
  const eligibleDocuments = useMemo(
    () =>
      (documentsData?.results || [])
        .filter(
          (doc) =>
            OCR_ELIGIBLE_DOCUMENT_TYPES.includes(doc.type) &&
            // A still-uploading drawer row has no size yet, and picking one would 404 rather than submit.
            doc.file_size_in_bytes != null,
        )
        .sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime(),
        ),
    [documentsData],
  );

  // Scoped to the whole file, not just this MSA, so a job on another MSA
  // for the same file can also block or be redirected to.
  const [pollingInterval, setPollingInterval] = useState(5000);
  const {
    data: jobsResponse,
    isSuccess: isJobsLoaded,
    isError: isJobsError,
  } = useGetBackgroundJobsQuery(
    { filters: { file_id: decision?.file } },
    {
      skip: !decision?.file || !isOpen,
      pollingInterval,
      // Without this, reopening the modal can serve a stale cached snapshot until the next poll tick.
      refetchOnMountOrArgChange: true,
    },
  );

  const ownJob = useMemo(
    () =>
      jobsResponse?.results?.find((j) => j.msa_decision_id === decisionId) ||
      null,
    [jobsResponse, decisionId],
  );
  const ownJobFailed = !!ownJob && isJobFailed(ownJob);
  // A decision with its own job already shows that, regardless of a
  // blocker elsewhere; only starting fresh or retrying needs the check.
  const wouldStartNewScan = !ownJob || (ownJobFailed && forcePicker);

  // Another still-undecided decision on this file with a non-failed job,
  // shown here instead of the one clicked so Process/Review/Try Again
  // always lands on whatever actually needs attention.
  const blockingDecision = useMemo(() => {
    if (!fileDecisions) return null;
    const seenDecisionIds = new Set<number>();
    for (const job of jobsResponse?.results || []) {
      if (
        job.msa_decision_id == null ||
        job.msa_decision_id === decisionId ||
        seenDecisionIds.has(job.msa_decision_id)
      ) {
        continue;
      }
      seenDecisionIds.add(job.msa_decision_id);
      const jobDecision = fileDecisions.find(
        (d) => d.id === job.msa_decision_id,
      );
      if (jobDecision?.decision_type === null && !isJobFailed(job)) {
        return jobDecision;
      }
    }
    return null;
  }, [fileDecisions, jobsResponse, decisionId]);
  const activeBlockingDecision = wouldStartNewScan ? blockingDecision : null;

  const effectiveDecision = activeBlockingDecision || decision;
  const effectiveDecisionId = effectiveDecision?.id ?? decisionId;
  const effectiveMsaId = activeBlockingDecision
    ? activeBlockingDecision.msa.toString()
    : msaId;

  const latestJob = activeBlockingDecision
    ? jobsResponse?.results?.find(
        (j) => j.msa_decision_id === effectiveDecisionId,
      ) || null
    : ownJob;
  // Falls back to the caller's already-confirmed payload while this modal's own fetch is still in flight, unless
  // something redirected to a different blocking decision.
  const reviewPayload =
    (latestJob?.review_payload as
      | MSADecisionReviewPayload
      | null
      | undefined) ??
    (!activeBlockingDecision ? initialReviewPayload : undefined);
  // Checked separately from reviewPayload, since a Textract failure never
  // produces one and we'd otherwise get stuck showing "still processing".
  const jobFailed = !!latestJob && isJobFailed(latestJob);
  // Also resolved on a bare "Success" with no payload, so a job that somehow finished without one never
  // polls forever instead of just showing an empty review.
  const isResolved =
    !latestJob ||
    jobFailed ||
    !!reviewPayload ||
    latestJob.status === "Success";

  // Only matters while about to let a new scan start; an error counts as settled too (no blocker below), so a
  // failed fetch doesn't strand the modal here forever.
  const fileDecisionsSettled = isFileDecisionsLoaded || isFileDecisionsError;
  const jobsSettled = isJobsLoaded || isJobsError;
  const isCheckingBlocker =
    wouldStartNewScan && !(fileDecisionsSettled && jobsSettled);

  useEffect(() => {
    if (isOpen) {
      setPollingInterval(isResolved ? 0 : 5000);
    }
  }, [isOpen, isResolved]);

  const handleSubmit = async () => {
    if (!selectedDocumentId) {
      KaboodleToaster.error("Please select a document to process");
      return;
    }

    try {
      setIsSubmitting(true);

      await submitOCRMSADecision({
        msaDecisionId: effectiveDecisionId,
        documentId: selectedDocumentId,
      }).unwrap();

      KaboodleToaster.success(
        "Document queued for OCR processing. A review button will appear on this page once it is done.",
      );
      handleClose();
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.error ||
          error?.message ||
          "Failed to submit document. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDocumentId(null);
    setForcePicker(false);
    onClose();
  };

  const isBusy = isLoading || isSubmitting;

  if (effectiveDecision && reviewPayload && !jobFailed) {
    return (
      <KaboodleModal isOpen={isOpen} onClose={handleClose} size="large">
        <Card heading="Review CMS Decision Letter">
          {activeBlockingDecision && (
            <p>
              This is a different decision letter that needs attention first.
            </p>
          )}
          <OCRMSADecisionReviewForm
            msaId={effectiveMsaId}
            decision={effectiveDecision}
            reviewPayload={reviewPayload}
            onSaved={handleClose}
            onCancel={handleClose}
          />
        </Card>
      </KaboodleModal>
    );
  }

  const showPicker =
    wouldStartNewScan && !isCheckingBlocker && !activeBlockingDecision;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      footer={
        showPicker && (
          <div className="flex jc-c">
            <Button
              onClick={handleSubmit}
              disabled={isBusy || !selectedDocumentId}
            >
              {isBusy ? "Submitting..." : "Submit"}
            </Button>
            <Button onClick={handleClose} disabled={isBusy}>
              Cancel
            </Button>
          </div>
        )
      }
    >
      <Card heading="Process CMS Decision Letter">
        <div className="OCRMSADecisionModal">
          {isCheckingBlocker ? (
            <div className="data-values">
              <div>Checking for other in-progress decision letters...</div>
            </div>
          ) : showPicker ? (
            <div className="data-values">
              {isDocumentsLoading ? (
                <div>Loading documents...</div>
              ) : eligibleDocuments.length === 0 ? (
                <p>No MSA Approval or Counter documents found for this file.</p>
              ) : (
                <>
                  <label>CMS Decision Letter</label>
                  <div className="ocr-document-list">
                    <div className="ocr-document-row ocr-document-row--header">
                      <span />
                      <span>Document</span>
                      <span>Type</span>
                      <span>Uploaded</span>
                    </div>
                    {eligibleDocuments.map((doc) => (
                      <label
                        key={doc.id}
                        className="ocr-document-row"
                        htmlFor={`ocr-document-${doc.id}`}
                      >
                        <input
                          type="radio"
                          id={`ocr-document-${doc.id}`}
                          name="ocr-msa-decision-document"
                          checked={selectedDocumentId === doc.id}
                          onChange={() => setSelectedDocumentId(doc.id)}
                        />
                        <span>
                          {doc.original_filename ||
                            doc.original_filename_as_uploaded}
                        </span>
                        <span>
                          {DOCUMENT_TYPE_LABELS[doc.type] || doc.type}
                        </span>
                        <span>{formatDate(doc.created)}</span>
                      </label>
                    ))}
                  </div>
                  <p>
                    This document will be OCR-ed and pre-emptively matched
                    against your proposed allocations for your review.
                  </p>
                </>
              )}
            </div>
          ) : jobFailed ? (
            <div className="data-values">
              <p>
                {latestJob?.status_message ||
                  "Something went wrong processing this letter."}
              </p>
              <div className="flex jc-c">
                <Button onClick={() => setForcePicker(true)}>Try Again</Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="data-values">
              {activeBlockingDecision && (
                <p>
                  This is a different decision letter that needs attention
                  first.
                </p>
              )}
              <p>
                This letter is still processing. You can navigate away, the
                review will be ready here once it is done.
              </p>
              <Button onClick={handleClose}>Close</Button>
            </div>
          )}
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default OCRMSADecisionModal;
